import React from 'react';

/**
 * Wraps React.lazy() with retry logic to handle stale chunk errors after deployments.
 *
 * When a new deployment changes chunk hashes, users with cached main bundles will
 * try to load old chunk files that no longer exist (404). This utility detects that
 * failure and forces a single page reload to fetch the updated main bundle.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const storageKey = 'chunk_reload_attempted';

    try {
      const module = await importFn();
      // Success — clear any previous reload flag
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      // Only attempt one reload per session to avoid infinite loops
      if (!sessionStorage.getItem(storageKey)) {
        sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise(() => {});
      }
      // Already tried reloading — rethrow so error boundaries can handle it
      throw error;
    }
  });
}
